import { Focusable, NumberFieldProps } from 'react-aria-components';

import { merge } from '@/components';
import {
  Badge,
  BasicTooltip,
  Button,
  Flex,
  FlexProps,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  NumberField,
  Popover,
  Select,
  SelectProps,
  SelectValue,
  Slider,
  SliderProps,
  Switch,
  SwitchProps,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@/components/core';
import { ChevronIcon, ChevronSelectorIcon, WarningDuotoneIcon } from '@/components/icons';
import { toNumber } from '@/utils';

type OptionProps = FlexProps;

const Option = ({ children, className, ...rest }: OptionProps) => {
  return (
    <Flex {...rest} className={merge('w-full items-center', className)}>
      {children}
    </Flex>
  );
};

export type OptionLabelProps = {
  label: string;
  description?: string;
  tooltip?: string;
};

export const OptionLabel = ({ label, description, tooltip }: OptionLabelProps) => {
  return (
    <div className="gap-xs flex grow flex-col justify-start">
      <div className="gap-xs flex">
        <Label>{label}</Label>
        {tooltip && (
          <BasicTooltip>
            <Text>{tooltip}</Text>
          </BasicTooltip>
        )}
      </div>
      {description && (
        <Text variant="tertiary" slot="description">
          {description}
        </Text>
      )}
    </div>
  );
};
export function NumericOption({
  label,
  description,
  tooltip,
  defaultValue,
  value,
  minValue = 0,
  maxValue,
  step,
  onChange,
  ...rest
}: OptionLabelProps &
  Pick<NumberFieldProps, 'value' | 'onChange' | 'defaultValue' | 'minValue' | 'maxValue' | 'step'>) {
  return (
    <Option {...rest}>
      <OptionLabel label={label} tooltip={tooltip} description={description} />

      <NumberField
        minValue={minValue}
        maxValue={maxValue}
        step={step}
        value={value}
        className="max-w-16"
        onChange={onChange}
        defaultValue={defaultValue}
      >
        <div className="relative max-w-full">
          <Input placeholder="2" className="inset-0 !w-full" />

          <Flex col className="px-xs absolute inset-y-0 right-0 h-full justify-center">
            <Button slot="increment" className="m-0 p-0">
              <ChevronIcon rotation="up" className="!size-3" />
            </Button>
            <Button slot="decrement" className="m-0 p-0">
              <ChevronIcon rotation="down" className="!size-3" />
            </Button>
          </Flex>
        </div>
      </NumberField>
    </Option>
  );
}

interface SelectItem<T> {
  id: string;
  name?: string;
  value?: T;
  description?: string;
  icon?: React.ReactNode;
  isDisabled?: boolean;
  isDefault?: boolean;
  hasNewVersion?: boolean;
  hasNewVersionTooltip?: string;
  onChange?: (value: T) => void;
}

export interface SelectOptionProps<T> extends SelectProps, OptionLabelProps {
  items: Iterable<SelectItem<T>>;
}

export function SelectOption<T>({
  items,
  defaultSelectedKey,
  value,
  label,
  description,
  tooltip,
  col,
  onChange,
  ...rest
}: SelectOptionProps<T> & OptionProps) {
  return (
    <Option {...rest} col={col}>
      <OptionLabel label={label} tooltip={tooltip} description={description} />

      <Select value={value} defaultSelectedKey={defaultSelectedKey} onChange={onChange} className={col ? 'w-full' : ''}>
        <Button variant="secondary">
          <SelectValue>
            {({ selectedItem }) => {
              const item = selectedItem as SelectItem<T>;
              return (
                <Flex className="gap-sm w-full items-center" variant="plain">
                  {item?.icon && <span>{item.icon}</span>}

                  <Text className="text-start">{item?.name}</Text>

                  {item?.hasNewVersion ? (
                    <TooltipTrigger>
                      <Focusable>
                        <WarningDuotoneIcon variant="warning" />
                      </Focusable>

                      <Tooltip variant="primary">
                        <Text>{item?.hasNewVersionTooltip ?? 'This setting has a new recommended version'}</Text>
                      </Tooltip>
                    </TooltipTrigger>
                  ) : null}

                  <div className="grow" />

                  {item?.isDefault ? (
                    <Badge variant="primary" borderless>
                      <Text>Default</Text>
                    </Badge>
                  ) : null}
                </Flex>
              );
            }}
          </SelectValue>
          <ChevronSelectorIcon />
        </Button>

        <Popover>
          <ListBox items={items}>
            {(option) => (
              <ListBoxItem id={option.id} key={option.id} isDisabled={option.isDisabled}>
                <Flex className="gap-sm w-full items-center">
                  {option.icon ?? null}
                  <Text className="grow aria-disabled:text-gray-400" aria-disabled={option.isDisabled}>
                    {option?.name}
                  </Text>

                  {option.isDefault ? (
                    <Badge variant="primary" className="ml-sm" borderless>
                      <Text>Default</Text>
                    </Badge>
                  ) : null}
                </Flex>
              </ListBoxItem>
            )}
          </ListBox>
        </Popover>
      </Select>
    </Option>
  );
}

type BooleanOptionProps = OptionLabelProps & Omit<OptionProps, 'onChange'> & SwitchProps;

export const BooleanOption = ({ onChange, isSelected, col, ...rest }: BooleanOptionProps) => {
  return (
    <Option>
      <Flex className="w-full">
        <Flex className="grow" col={col}>
          <OptionLabel {...rest} />
        </Flex>
        <Switch onChange={onChange} isSelected={isSelected} />
      </Flex>
    </Option>
  );
};

type SliderOptionProps = OptionLabelProps &
  SliderProps & {
    minValueLabel?: string;
    maxValueLabel?: string;
  };

export const SliderOption = ({
  minValue,
  maxValue,
  minValueLabel,
  maxValueLabel,
  step,
  value,
  onChange,
  ...rest
}: SliderOptionProps) => {
  return (
    <Option className="gap-sm items-start" col>
      <Flex className="w-full">
        <OptionLabel {...rest} />
        <span className="grow" />
        <span className="px-sm flex items-center rounded-xs border border-gray-200 text-sm">
          {toNumber(value)?.toFixed(2)}
        </span>
      </Flex>

      <Slider
        className="w-full"
        minValue={minValue ?? 0}
        maxValue={maxValue ?? 1}
        value={value}
        step={step ?? 0.01}
        onChange={onChange}
      />

      <div className="flex w-full items-center">
        {minValueLabel ? (
          <Text size="xs" className="text-gray-500">
            {minValueLabel}
          </Text>
        ) : null}

        <div className="grow" />

        {maxValueLabel ? (
          <Text size="xs" className="text-gray-500">
            {maxValueLabel}
          </Text>
        ) : null}
      </div>
    </Option>
  );
};

export const OptionGroup = ({ children, className }: FlexProps) => (
  <Flex col className={merge('gap-md', className)}>
    {children}
  </Flex>
);
